import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import NoticeDetailsModal from './parts/NoticeDetailsModal';
import AddNoticeModal from './parts/AddNoticeModal';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';

/* ─── Design Tokens ────────────────────────────────────────────────── */
const C = {
  navy: '#0e225d',
  navyMid: '#1a3370',
  navyHover: 'rgba(14,34,93,0.06)',
  gold: '#fbb21f',
  goldDim: 'rgba(251,178,31,0.14)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff',
  border: '#e5e7eb',
  muted: '#9ca3af',
  sub: '#6b7280',
  body: '#1f2937',
  green: '#16a34a',
  greenDim: 'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red: '#dc2626',
  redDim: 'rgba(220,38,38,0.08)',
  redBorder: 'rgba(220,38,38,0.25)',
};

interface Notice {
  notice_id: string;
  institute_id: string;
  branch_id: string;
  batch_id: string | null;
  broadcast_type: string;
  sent_to: string[];
  notice_title: string;
  notice_body: string;
  files: string[];
  created_at: string;
  created_by: string;
}

interface ApiResponse {
  success: boolean;
  data: Notice[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

const NoticeBoard: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [selectedNotices, setSelectedNotices] = useState<Notice[] | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedNoticeId, setSelectedNoticeId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);

  const { profile } = useOperatorProfileStore();

  useEffect(() => {
    if (profile?.branch_id) {
      fetchNotices();
    }
  }, [page, limit, profile]);

  const fetchNotices = async () => {
    if (!profile?.branch_id) {
      setError('Branch information not available');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      const response = await apiClient.get<ApiResponse>(
        `/v1/noticeboard/by-branch/${profile.branch_id}?${params}`
      );

      if (response.data.success) {
        setNotices(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load notices');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load notices');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotice = async (noticeId: string) => {
    if (!window.confirm('Are you sure you want to delete this notice?')) return;

    try {
      const response = await apiClient.delete(`/v1/noticeboard/${noticeId}`);
      if (response.data.success) {
        fetchNotices();
      } else {
        setError('Failed to delete notice');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete notice');
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedNotices || selectedNotices.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedNotices.length} notice(s)?`)) return;

    try {
      await Promise.all(
        selectedNotices.map(notice =>
          apiClient.delete(`/v1/noticeboard/${notice.notice_id}`)
        )
      );
      setSelectedNotices(null);
      fetchNotices();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete notices');
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const broadcastTypeTemplate = (rowData: Notice) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      branch: { bg: C.goldDim, text: '#b8860b', border: C.goldBorder },
      batch:  { bg: 'rgba(14,34,93,0.1)', text: C.navy, border: 'rgba(14,34,93,0.25)' },
      individual: { bg: C.redDim, text: C.red, border: C.redBorder },
    };

    const color = colors[rowData.broadcast_type] || {
      bg: 'rgba(100,116,139,0.1)', text: '#475569', border: 'rgba(100,116,139,0.3)',
    };

    return (
      <span style={{
        padding: '3px 10px',
        borderRadius: '20px',
        fontSize: '0.71rem',
        fontWeight: '700',
        background: color.bg,
        color: color.text,
        border: `1px solid ${color.border}`,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        display: 'inline-block',
      }}>
        {rowData.broadcast_type}
      </span>
    );
  };

  const noticeBodyTemplate = (rowData: Notice) => {
    const maxLength = 60;
    const body = rowData.notice_body || '—';
    return (
      <div style={{
        maxWidth: '300px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: C.sub,
        fontSize: '0.83rem',
      }}>
        {body.length > maxLength ? `${body.substring(0, maxLength)}...` : body}
      </div>
    );
  };

  const actionBodyTemplate = (rowData: Notice) => (
    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
      <button
        onClick={() => { setSelectedNoticeId(rowData.notice_id); setShowDetailsModal(true); }}
        title="View Details"
        style={{
          padding: '6px 12px',
          height: '32px',
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: '8px',
          cursor: 'pointer',
          color: C.muted,
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          fontSize: '0.75rem',
          fontWeight: '600',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(14,34,93,0.08)';
          e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)';
          e.currentTarget.style.color = C.navy;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = C.border;
          e.currentTarget.style.color = C.muted;
        }}
      >
        <i className="pi pi-eye" style={{ fontSize: '0.8rem' }} />
        View
      </button>

      <button
        onClick={() => { setEditingNotice(rowData); setShowAddModal(true); }}
        title="Edit Notice"
        style={{
          padding: '6px 12px',
          height: '32px',
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: '8px',
          cursor: 'pointer',
          color: C.muted,
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          fontSize: '0.75rem',
          fontWeight: '600',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(251,178,31,0.12)';
          e.currentTarget.style.borderColor = 'rgba(251,178,31,0.35)';
          e.currentTarget.style.color = '#b8860b';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = C.border;
          e.currentTarget.style.color = C.muted;
        }}
      >
        <i className="pi pi-pencil" style={{ fontSize: '0.8rem' }} />
        Edit
      </button>

      <button
        onClick={() => handleDeleteNotice(rowData.notice_id)}
        title="Delete Notice"
        style={{
          padding: '6px 12px',
          height: '32px',
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: '8px',
          cursor: 'pointer',
          color: C.muted,
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          fontSize: '0.75rem',
          fontWeight: '600',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(220,38,38,0.08)';
          e.currentTarget.style.borderColor = 'rgba(220,38,38,0.25)';
          e.currentTarget.style.color = C.red;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = C.border;
          e.currentTarget.style.color = C.muted;
        }}
      >
        <i className="pi pi-trash" style={{ fontSize: '0.8rem' }} />
        Delete
      </button>
    </div>
  );

  const renderPagination = () => {
    const pages: (number | string)[] = [];
    const total = pagination.pages;

    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, '...', total);
    } else if (page >= total - 2) {
      pages.push(1, '...', total - 2, total - 1, total);
    } else {
      pages.push(1, '...', page, '...', total);
    }

    return pages.map((p, i) =>
      p === '...' ? (
        <span key={`e${i}`} style={{ padding: '0 4px', color: C.muted, fontSize: '0.82rem' }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => setPage(p as number)}
          style={{
            minWidth: '32px',
            height: '32px',
            padding: '0 8px',
            borderRadius: '7px',
            fontSize: '0.82rem',
            fontWeight: page === p ? '700' : '500',
            background: page === p ? C.navy : 'transparent',
            color: page === p ? C.gold : C.navy,
            border: page === p ? 'none' : `1px solid ${C.border}`,
            boxShadow: page === p ? '0 2px 6px rgba(14,34,93,0.25)' : 'none',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => { if (page !== p) e.currentTarget.style.background = C.navyHover; }}
          onMouseLeave={(e) => { if (page !== p) e.currentTarget.style.background = 'transparent'; }}
        >
          {p}
        </button>
      )
    );
  };

  if (loading && notices.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading notices…</p>
        </div>
      </div>
    );
  }

  const selected = selectedNotices ?? [];

  return (
    <>
      <style>{`
        .nb-datatable .p-datatable-thead > tr > th {
          background: #fafafa !important;
          color: ${C.muted} !important;
          font-size: 0.7rem !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.6px !important;
          border-bottom: 2px solid ${C.border} !important;
          padding: 12px 14px !important;
        }
        .nb-datatable .p-datatable-tbody > tr { transition: background 0.12s; }
        .nb-datatable .p-datatable-tbody > tr:hover > td { background: rgba(14,34,93,0.025) !important; }
        .nb-datatable .p-datatable-tbody > tr > td {
          border-bottom: 1px solid #f3f4f6 !important;
          padding: 10px 12px !important;
          font-size: 0.83rem;
        }
        .nb-datatable .p-checkbox .p-checkbox-box.p-highlight { background: ${C.navy} !important; border-color: ${C.navy} !important; }
        .nb-datatable .p-checkbox .p-checkbox-box:hover { border-color: ${C.gold} !important; }
        .nb-datatable .p-datatable-loading-overlay { background: rgba(255,255,255,0.7) !important; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error && <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.88rem' }} />}

        {/* Main Table Card */}
        <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', overflow: 'hidden' }}>

          {/* Table Header Row */}
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.8rem', color: C.sub }}>Showing per page</span>
              <select
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                style={{
                  padding: '4px 8px',
                  border: `1px solid ${C.border}`,
                  borderRadius: '7px',
                  fontSize: '0.8rem',
                  color: C.navy,
                  background: C.white,
                  fontWeight: '600',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                {[5, 10, 20, 50].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>

            {/* Add Notice Button */}
            <button
              onClick={() => { setEditingNotice(null); setShowAddModal(true); }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                padding: '10px 18px',
                background: C.navy,
                border: 'none',
                borderRadius: '9px',
                color: C.white,
                fontSize: '0.82rem',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(14,34,93,0.25)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = C.navyMid; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = C.navy; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <i className="pi pi-plus" style={{ fontSize: '0.82rem' }} />
              Add New Notice
            </button>

            {/* Bulk Action */}
            {selected.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: '#f8f9ff', border: `1px solid ${C.border}`, borderRadius: '10px' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: '700', color: C.navy }}>{selected.length} Selected</span>
                <button
                  onClick={handleBulkDelete}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 12px',
                    background: C.redDim,
                    border: `1px solid ${C.redBorder}`,
                    borderRadius: '7px',
                    color: C.red,
                    fontSize: '0.78rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >
                  <i className="pi pi-trash" style={{ fontSize: '0.72rem' }} />
                  Bulk Delete
                </button>
              </div>
            )}
          </div>

          {/* DataTable */}
          <DataTable
            value={notices}
            selection={selectedNotices}
            onSelectionChange={(e: any) => setSelectedNotices(e.value)}
            dataKey="notice_id"
            loading={loading}
            emptyMessage={
              <div style={{ textAlign: 'center', padding: '40px 20px', color: C.muted }}>
                <i className="pi pi-bell" style={{ fontSize: '2rem', display: 'block', marginBottom: '10px', opacity: 0.4 }} />
                <span style={{ fontSize: '0.9rem' }}>No notices found</span>
              </div>
            }
            className="nb-datatable"
            style={{ fontSize: '0.83rem' }}
          >
            <Column selectionMode="multiple" headerStyle={{ width: '3rem', padding: '12px 14px' }} bodyStyle={{ padding: '12px 14px' }} />
            <Column
              field="notice_title"
              header="Notice Title"
              headerStyle={{ minWidth: '280px' }}
              body={(row) => <span style={{ fontWeight: '600', color: C.navy, fontSize: '0.84rem' }}>{row.notice_title}</span>}
            />
            <Column field="notice_body" header="Notice Body" body={noticeBodyTemplate} />
            <Column field="broadcast_type" header="Type" body={broadcastTypeTemplate} headerStyle={{ minWidth: '150px' }} />
            <Column
              field="created_at"
              header="Created At"
              body={(row) => <span style={{ color: C.sub, fontSize: '0.83rem' }}>{formatDateTime(row.created_at)}</span>}
            />
            <Column header="Actions" body={actionBodyTemplate} headerStyle={{ minWidth: '260px', textAlign: 'center' }} bodyStyle={{ textAlign: 'center' }} />
          </DataTable>

          {/* Pagination Footer */}
          {pagination.pages > 0 && (
            <div style={{ padding: '14px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <span style={{ fontSize: '0.8rem', color: C.muted }}>
                Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total} notices
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}
                >
                  <i className="pi pi-angle-double-left" style={{ fontSize: '0.7rem' }} />
                </button>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}
                >
                  <i className="pi pi-angle-left" style={{ fontSize: '0.7rem' }} />
                </button>

                {renderPagination()}

                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', opacity: page === pagination.pages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}
                >
                  <i className="pi pi-angle-right" style={{ fontSize: '0.7rem' }} />
                </button>
                <button
                  onClick={() => setPage(pagination.pages)}
                  disabled={page === pagination.pages}
                  style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', opacity: page === pagination.pages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}
                >
                  <i className="pi pi-angle-double-right" style={{ fontSize: '0.7rem' }} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <NoticeDetailsModal
        visible={showDetailsModal}
        noticeId={selectedNoticeId}
        onHide={() => setShowDetailsModal(false)}
      />

      {profile?.institute_id && profile?.branch_id && (
        <AddNoticeModal
          visible={showAddModal}
          instituteId={profile.institute_id}
          branchId={profile.branch_id}
          editingNotice={editingNotice}
          onHide={() => { setShowAddModal(false); setEditingNotice(null); }}
          onSuccess={() => { fetchNotices(); }}
        />
      )}
    </>
  );
};

export default NoticeBoard;